import React from 'react'
import { type BadgeKindValue } from '@mc/design-system'
import { useTranslation, withI18nContext } from '@mc/i18n'
import {
  BenefitTrans,
  Table,
  TableFeature,
  TableHeader,
  useTiersInfo,
  type PlansTier,
} from '@mc/plans-ui'
import { PricingCell } from '../../../../../../components/Matrix/features/PricingFeature/components/DefaultPricingFeature/PricingCell'
import { CHECKOUT_I18N_NAMESPACE } from '../../../../../../i18n'
import { type Variant2Layout } from '../../../../../prcng-1266_v1_cm_dual_cta'
import { useMCProCertsCatalogValue } from '../../../../hooks/useMCProCertsCatalogValue'
import { MC_PRO_TIER_I18N_NAMESPACE, i18nInstance } from '../../../../i18n'
import { CertificatesFeature } from '../../../CertificatesFeature'
import { OfflineDownloadIcon } from '../features'

type ProposalLayout = Exclude<Variant2Layout, 'current'>

type Column = 'individual' | 'duo' | 'family' | 'familyPlus'

const SPACING = 3

const TITLES = [
  ['individual', TableHeader.IndividualTitle],
  ['duo', TableHeader.DuoTitle],
  ['family', TableHeader.FamilyTitle],
  ['familyPlus', TableHeader.FamilyPlusTitle],
] as const

const ACTIONS = [
  ['individual', TableHeader.IndividualAction],
  ['duo', TableHeader.DuoAction],
  ['family', TableHeader.FamilyAction],
  ['familyPlus', TableHeader.FamilyPlusAction],
] as const

const CELLS = [
  ['individual', TableFeature.Individual],
  ['duo', TableFeature.Duo],
  ['family', TableFeature.Family],
  ['familyPlus', TableFeature.FamilyPlus],
] as const

type ColumnBadge = { text: string; kind?: BadgeKindValue }

const useBadges = (
  layout: ProposalLayout,
): Partial<Record<Column, ColumnBadge>> => {
  const { t } = useTranslation(MC_PRO_TIER_I18N_NAMESPACE)
  const { t: tCheckout } = useTranslation(CHECKOUT_I18N_NAMESPACE)
  const bestValue = { text: tCheckout('plans.headers.bestValue') }

  return layout === 'premium_best_value'
    ? {
        family: bestValue,
        familyPlus: { text: t('matrix.headers.forProfessionals'), kind: 'dim' },
      }
    : { duo: { text: t('matrix.headers.mostPopular') }, familyPlus: bestValue }
}

const AccountAndDevices = ({ tier }: { tier: PlansTier }) => {
  const { t } = useTranslation(MC_PRO_TIER_I18N_NAMESPACE)

  return (
    <>
      {t('matrix.headers.oneAccount')}
      <br />
      <BenefitTrans
        ns={CHECKOUT_I18N_NAMESPACE}
        i18nKey='plans.headers.devicesInfo'
        tier={tier}
        name='concurrent_devices'
      />
    </>
  )
}

const ProDescription = () => {
  const { t } = useTranslation(MC_PRO_TIER_I18N_NAMESPACE)
  const value = useMCProCertsCatalogValue()

  return (
    <>
      {t('matrix.headers.everythingInPremium')}
      <br />
      {value
        ? t('matrix.headers.plusAllCertificatesValue', { value })
        : t('matrix.headers.plusAllCertificates')}
    </>
  )
}

const Header = ({ layout }: { layout: ProposalLayout }) => {
  const { t } = useTranslation(CHECKOUT_I18N_NAMESPACE)
  const tiers = useTiersInfo()
  const badges = useBadges(layout)

  return (
    <>
      <TableHeader.Titles>
        {TITLES.map(([column, Title]) => (
          <Title key={column} spacing={SPACING}>
            {tiers[column].displayName}
          </Title>
        ))}
      </TableHeader.Titles>

      <TableHeader.Actions>
        {ACTIONS.map(([column, Action]) => (
          <Action
            key={column}
            spacing={SPACING}
            badge={badges[column]?.text}
            badgeKind={badges[column]?.kind}
            description={
              column === 'familyPlus' ? (
                <ProDescription />
              ) : (
                <AccountAndDevices tier={tiers[column]} />
              )
            }
            label={t('plans.headers.cta.label', {
              plan: tiers[column].displayName,
            })}
            selectedLabel={t('plans.headers.cta.selected')}
            unselectedLabel={t('plans.headers.cta.select')}
          />
        ))}
      </TableHeader.Actions>
    </>
  )
}

const Row = ({
  title,
  subtitle,
  children,
}: {
  title: string
  subtitle?: string
  children: (tier: PlansTier) => React.ReactNode
}) => {
  const tiers = useTiersInfo()

  return (
    <TableFeature.Feature>
      <TableFeature.Label title={title} subtitle={subtitle} />
      {CELLS.map(([column, Cell]) => (
        <Cell key={column} spacing={SPACING}>
          {children(tiers[column])}
        </Cell>
      ))}
    </TableFeature.Feature>
  )
}

export const ProposalMatrix = withI18nContext(i18nInstance)(({
  layout,
}: {
  layout: ProposalLayout
}) => {
  const { t } = useTranslation(MC_PRO_TIER_I18N_NAMESPACE)
  const { t: tCheckout } = useTranslation(CHECKOUT_I18N_NAMESPACE)

  return (
    <Table label={tCheckout('plans.label')}>
      <TableHeader.Header>
        <Header layout={layout} />
      </TableHeader.Header>

      <TableFeature.FeatureList>
        <Row title={tCheckout('plans.features.pricing.title')}>
          {(tier) => <PricingCell tier={tier} />}
        </Row>
        <Row
          title={tCheckout('plans.features.devices.title')}
          subtitle={tCheckout('plans.features.devices.subtitle')}
        >
          {(tier) => (
            <BenefitTrans
              tier={tier}
              name='concurrent_devices'
              i18nKey='plans.features.devices.counter'
              ns={CHECKOUT_I18N_NAMESPACE}
            />
          )}
        </Row>
        <Row title={tCheckout('plans.features.offlineDownload.title')}>
          {(tier) => <OfflineDownloadIcon tier={tier} />}
        </Row>
        <CertificatesFeature
          title={t('matrix.certificates.annualPass.title')}
          description={t('matrix.certificates.annualPass.shortDescription')}
          values={['notIncluded', 'notIncluded', 'notIncluded', 'included']}
          rowPositionIndex={4}
        />
      </TableFeature.FeatureList>
    </Table>
  )
})
